/**
 * AudioDigestsPage — Enhanced Audio Summaries Archive
 * Full controls: Play, Download, Delete + specialty/date filtering
 *
 * Behind ENABLE_LIBRARY_AUDIO_DIGESTS_V2
 * PHI-Zero: no user-entered text is logged.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useFeatureFlags, useFeatureFlag } from '../contexts/FeatureFlagsContext';
import { audioDigestsAPI, articlesAPI, preferencesAPI, libraryAPI, workspaceAPI } from '../api';
import { BACKEND_URL } from 'apiConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import PlaylistModal from '../components/PlaylistModal';
import { SharedDigestFilterBar, type DigestProfileStat } from '../components/shared/SharedDigestFilterBar';
import { itemMatchesDigestProfileWithLibrary, type DigestMatchableItem } from '../utils/digestArticleMatch';
import {
  getPersistedDigestProfileId,
  setPersistedDigestProfileId,
} from '../utils/sharedDigestSelection';
import { mapApiArticleToRecord } from '../utils/sourcePermissions';
import { CrossAppLinks } from '../components/CrossAppLinks';
import { toast } from 'sonner';
import {
  ArrowLeft, Headphones, Download, Calendar, Play, Loader2,
  BookOpen, Clock, Music, Filter, X, Stethoscope, Trash2, Pause, FileText
} from 'lucide-react';

// ── History list ────────────────────────────────────────────────────────────

const AudioDigestsList: React.FC = () => {
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const routeParams = useParams<{ profileId?: string; pmid?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDigestRoute = navV2Enabled && location.pathname.includes('/audio-digests/digests/');
  const isArticleRoute = navV2Enabled && location.pathname.includes('/audio-digests/articles/');

  const [digests, setDigests] = useState<any[]>([]);
  const [articleAudios, setArticleAudios] = useState<any[]>([]);
  const [libraryByPmid, setLibraryByPmid] = useState<Map<string, DigestMatchableItem>>(new Map());
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteTitle, setConfirmDeleteTitle] = useState<string>('');
  
  // Digest filter state
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    () => getPersistedDigestProfileId()
  );
  const [profileStats, setProfileStats] = useState<DigestProfileStat[]>([]);
  const [profileStatsLoading, setProfileStatsLoading] = useState(true);

  const loadProfileStats = useCallback(async () => {
    try {
      setProfileStatsLoading(true);
      const response = await preferencesAPI.getProfilesScreenStats();
      setProfileStats(response.profiles || []);
    } catch (error) {
      console.error('Failed to load profile stats:', error);
    } finally {
      setProfileStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfileStats();
  }, [loadProfileStats]);

  const selectedProfile = useMemo(
    () => profileStats.find((p) => p.profile_id === selectedProfileId) ?? null,
    [profileStats, selectedProfileId]
  );

  const matchingLibraryPmids = useMemo(() => {
    if (!selectedProfile) return new Set<string>();
    const pmids = new Set<string>();
    libraryByPmid.forEach((record, pmid) => {
      if (itemMatchesDigestProfileWithLibrary({ ...record, pmid }, selectedProfile, libraryByPmid)) {
        pmids.add(pmid);
      }
    });
    return pmids;
  }, [libraryByPmid, selectedProfile]);

  const [digestQueuePmids, setDigestQueuePmids] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedProfile?.last_digest_id) {
      setDigestQueuePmids(new Set());
      return;
    }
    let cancelled = false;
    workspaceAPI
      .getScreenQueue({
        digest_id: selectedProfile.last_digest_id,
        status: 'all',
        limit: 500,
      })
      .then((res) => {
        if (cancelled) return;
        setDigestQueuePmids(
          new Set((res.articles || []).map((a) => String(a.pmid)).filter(Boolean))
        );
      })
      .catch(() => {
        if (!cancelled) setDigestQueuePmids(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProfile?.last_digest_id, selectedProfile?.profile_id]);

  const pmidBelongsToSelectedDigest = useCallback(
    (pmid: string | null | undefined) => {
      if (!selectedProfileId) return true;
      if (!selectedProfile) return false;
      if (!pmid) return false;
      const key = String(pmid);
      if (matchingLibraryPmids.has(key)) return true;
      if (digestQueuePmids.has(key)) return true;
      return false;
    },
    [selectedProfileId, selectedProfile, matchingLibraryPmids, digestQueuePmids]
  );

  const itemBelongsToSelectedDigest = useCallback(
    (item: DigestMatchableItem & { pmid?: string | null }) => {
      if (!selectedProfileId) return true;
      if (!selectedProfile) return false;
      if (item.pmid && pmidBelongsToSelectedDigest(item.pmid)) return true;
      return itemMatchesDigestProfileWithLibrary(item, selectedProfile, libraryByPmid);
    },
    [selectedProfileId, selectedProfile, pmidBelongsToSelectedDigest, libraryByPmid]
  );

  // Inline player state for quick play from list
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Filtering state
  const [specialtyFilter, setSpecialtyFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  // Tab state: 'articles' (individual) or 'digests' (combined)
  const [activeTab, setActiveTab] = useState<'articles' | 'digests'>('articles');

  // ── Nav V2: Route-param sync (flag-gated) ──
  useEffect(() => {
    if (!navV2Enabled) return;
    if (isDigestRoute && routeParams.profileId) {
      setSelectedProfileId(routeParams.profileId);
      setPersistedDigestProfileId(routeParams.profileId);
    } else if (isArticleRoute) {
      // Article route: switch to articles tab, use profileId from query if provided
      setActiveTab('articles');
      const qpProfileId = searchParams.get('profileId');
      if (qpProfileId) {
        setSelectedProfileId(qpProfileId);
        setPersistedDigestProfileId(qpProfileId);
      }
    } else if (!isDigestRoute && !isArticleRoute) {
      const persisted = getPersistedDigestProfileId();
      if (persisted) {
        setSelectedProfileId(persisted);
        navigate(`/audio-digests/digests/${persisted}`, { replace: true });
      }
    }
  }, [navV2Enabled, isDigestRoute, isArticleRoute, routeParams.profileId, routeParams.pmid, location.pathname]);

  // Nav V2: Article focus — scroll to and highlight target after load
  const [focusedPmid, setFocusedPmid] = useState<string | null>(null);
  useEffect(() => {
    if (!navV2Enabled || !isArticleRoute || !routeParams.pmid) {
      setFocusedPmid(null);
      return;
    }
    setFocusedPmid(routeParams.pmid);
  }, [navV2Enabled, isArticleRoute, routeParams.pmid, location.pathname]);

  useEffect(() => {
    if (!focusedPmid || loading) return;
    const timeout = setTimeout(() => {
      const el = document.querySelector(`[data-testid="article-audio-card-${focusedPmid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-sky-400', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-sky-400', 'ring-offset-2'), 3000);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [focusedPmid, loading, articleAudios]);

  // Nav V2: Route-aware chip handler
  const handleProfileSelect = useCallback((id: string | null) => {
    setPersistedDigestProfileId(id);
    if (navV2Enabled) {
      navigate(id ? `/audio-digests/digests/${id}` : '/audio-digests');
    } else {
      setSelectedProfileId(id);
    }
  }, [navV2Enabled, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [articleRes, libraryRes, foldersRes] = await Promise.all([
        articlesAPI.getMyAudioSummaries().catch(() => ({ audio_summaries: [] })),
        libraryAPI.getLibrary({ limit: 500 }).catch(() => ({ articles: [] })),
        libraryAPI.getFolders().catch(() => ({ folders: [] })),
      ]);

      setArticleAudios(articleRes.audio_summaries || []);

      const folderProfiles = new Map<string, { profile_id?: string; specialty_id?: string }>();
      for (const folder of foldersRes.folders || []) {
        if (!folder.folder) continue;
        folderProfiles.set(folder.folder.toLowerCase(), {
          profile_id: folder.profile_id,
          specialty_id: folder.specialty_id,
        });
      }

      const libMap = new Map<string, DigestMatchableItem>();
      for (const raw of libraryRes.articles || []) {
        if (!raw?.pmid) continue;
        const record = mapApiArticleToRecord(raw as Record<string, unknown>);
        const folderMeta = record.folder
          ? folderProfiles.get(record.folder.toLowerCase())
          : undefined;
        libMap.set(String(raw.pmid), {
          ...record,
          profile_id: record.profile_id ?? folderMeta?.profile_id,
          specialty_id: record.specialty_id ?? folderMeta?.specialty_id,
        });
      }
      setLibraryByPmid(libMap);
    } catch {
      // Silently fail — user might not have any
    }
    if (flags.enable_library_audio_digests_v2) {
      try {
        const r = await audioDigestsAPI.list();
        setDigests(r.audio_digests || []);
      } catch {
        // Silently fail
      }
    }
    setLoading(false);
  }, [flags.enable_library_audio_digests_v2]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Extract unique specialties for filter dropdown
  const availableSpecialties = useMemo(() => {
    const specialties = new Map<string, string>();
    digests.forEach(d => {
      if (d.specialty_id && d.specialty_name) {
        specialties.set(d.specialty_id, d.specialty_name);
      }
    });
    return Array.from(specialties.entries()).map(([id, name]) => ({ id, name }));
  }, [digests]);

  // Filter digests based on selected filters and sort newest-to-oldest
  const filteredDigests = useMemo(() => {
    // First sort by created_at (newest first)
    const sorted = [...digests].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    });
    
    return sorted.filter(d => {
      const digestPmids = [
        d.pmid,
        ...(Array.isArray(d.pmids) ? d.pmids : []),
        ...(Array.isArray(d.items) ? d.items.map((i: { pmid?: string }) => i.pmid) : []),
      ].filter(Boolean);
      const matchesByPmid = digestPmids.some((pmid: string) => pmidBelongsToSelectedDigest(pmid));
      if (
        !matchesByPmid &&
        !itemBelongsToSelectedDigest({
          pmid: d.pmid,
          profile_id: d.profile_id,
          specialty_id: d.specialty_id,
          subspecialty_id: d.subspecialty_id,
          specialty_name: d.specialty_name,
        })
      ) {
        return false;
      }
      // Also filter by secondary specialty filter dropdown
      if (specialtyFilter !== 'all' && d.specialty_id !== specialtyFilter) return false;
      if (dateFilter !== 'all') {
        const createdDate = new Date(d.created_at);
        const now = new Date();
        const daysAgo = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dateFilter === '7' && daysAgo > 7) return false;
        if (dateFilter === '30' && daysAgo > 30) return false;
        if (dateFilter === '90' && daysAgo > 90) return false;
      }
      return true;
    });
  }, [digests, specialtyFilter, dateFilter, pmidBelongsToSelectedDigest, itemBelongsToSelectedDigest]);
  
  // Filter article audios by profile and sort newest-to-oldest
  const filteredArticleAudios = useMemo(() => {
    // Sort by created_at (newest first)
    const sorted = [...articleAudios].sort((a, b) => {
      const dateA = new Date(a.created_at || a.generated_at || 0).getTime();
      const dateB = new Date(b.created_at || b.generated_at || 0).getTime();
      return dateB - dateA;
    });
    
    if (!selectedProfileId) return sorted;
    return sorted.filter((a) =>
      itemBelongsToSelectedDigest({
        pmid: a.pmid,
        profile_id: a.profile_id,
        specialty_id: a.specialty_id,
        subspecialty_id: a.subspecialty_id,
        specialty_name: a.specialty_name,
        folder: a.folder,
      })
    );
  }, [articleAudios, selectedProfileId, itemBelongsToSelectedDigest]);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const clearFilters = () => {
    setSpecialtyFilter('all');
    setDateFilter('all');
  };

  const hasActiveFilters = specialtyFilter !== 'all' || dateFilter !== 'all';

  // Delete handler
  const handleDelete = async (audioDigestId: string) => {
    setDeletingId(audioDigestId);
    try {
      await audioDigestsAPI.delete(audioDigestId);
      // Optimistic remove from list
      setDigests(prev => prev.filter(d => d.audio_digest_id !== audioDigestId));
      // Stop player if playing this digest
      if (playingId === audioDigestId) {
        setPlayingId(null);
        setPlayingUrl(null);
        if (audioRef.current) audioRef.current.pause();
      }
      toast.success('Audio summary deleted');
    } catch {
      toast.error('Failed to delete audio summary');
    } finally {
      setDeletingId(null);
    }
  };

  // Quick play handler from list (for combined summaries that have a URL)
  const handleQuickPlay = async (digest: any) => {
    const isCombined = digest.mode === 'combined_summary';

    if (playingId === digest.audio_digest_id) {
      // Toggle pause/play
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
      return;
    }

    if (isCombined && digest.combined_audio_url) {
      setPlayingId(digest.audio_digest_id);
      setPlayingUrl(digest.combined_audio_url);
    } else if (!isCombined) {
      // For playlist mode, navigate to detail to use PlaylistModal
      navigate(`/audio-digests/${digest.audio_digest_id}`);
    } else {
      // Fetch detail to get the URL
      try {
        const detail = await audioDigestsAPI.get(digest.audio_digest_id);
        if (detail.combined_audio_url) {
          setPlayingId(digest.audio_digest_id);
          setPlayingUrl(detail.combined_audio_url);
        } else {
          toast.info('Audio is not ready yet');
        }
      } catch {
        navigate(`/audio-digests/${digest.audio_digest_id}`);
      }
    }
  };

  // Play handler for individual article audio
  const handlePlayArticle = (pmid: string, audioUrl: string) => {
    if (playingId === pmid) {
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
      return;
    }
    setPlayingId(pmid);
    setPlayingUrl(audioUrl);
  };

  // Download handler for individual article audio
  const handleDownloadArticle = async (pmid: string, audioUrl: string, title: string) => {
    try {
      const token = localStorage.getItem('token');
      const fullUrl = `${BACKEND_URL}${audioUrl}`;
      const resp = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        toast.error('Download failed');
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `litpulse_audio_${pmid}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('Download started');
    } catch {
      toast.error('Download failed');
    }
  };

  // Download handler
  const handleDownload = async (digest: any) => {
    const token = localStorage.getItem('token');
    const url = audioDigestsAPI.downloadZipUrl(digest.audio_digest_id);
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        if (body?.detail?.error_code === 'no_audio_ready') {
          toast.error('No audio tracks are ready yet.');
        } else {
          toast.error('Download failed');
        }
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `litpulse_audio_${digest.audio_digest_id.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('Download started');
    } catch {
      toast.error('Download failed');
    }
  };

  const pageLoading = loading || (Boolean(selectedProfileId) && profileStatsLoading);

  if (pageLoading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="audio-digests-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="audio-digests-page">
      {/* Digest Filter Bar */}
      <SharedDigestFilterBar
        selectedProfileId={selectedProfileId}
        onSelectProfile={handleProfileSelect}
        profiles={profileStats}
        statsLoading={profileStatsLoading}
        onRefreshStats={loadProfileStats}
        showUnscreenedBadge={false}
        showSavedPercent={true}
        showPrimaryStar={true}
        allLabel="All Specialties"
      />
      
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <Headphones className="h-6 w-6 text-sky-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="audio-digests-title">My Audio Summaries</h1>
              <p className="text-sm text-slate-500 mt-0.5">Your audio digest history with specialty tags</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Inline audio player */}
        {playingUrl && playingId && (
          <Card className="bg-sky-50 border-sky-200" data-testid="inline-audio-player">
            <CardContent className="p-3 flex items-center gap-3">
              <Headphones className="h-5 w-5 text-sky-600 shrink-0" />
              <audio
                ref={audioRef}
                controls
                autoPlay
                className="flex-1 h-9"
                src={playingUrl.startsWith('http') ? playingUrl : `${BACKEND_URL}${playingUrl}`}
                onEnded={() => { setPlayingId(null); setPlayingUrl(null); }}
              />
              <button
                onClick={() => { setPlayingId(null); setPlayingUrl(null); if (audioRef.current) audioRef.current.pause(); }}
                className="text-slate-400 hover:text-slate-600"
                data-testid="close-inline-player-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </CardContent>
          </Card>
        )}

        {/* Tab switcher */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={activeTab === 'articles' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('articles')}
            className={activeTab === 'articles' ? 'bg-sky-600 hover:bg-sky-700 text-white' : ''}
            data-testid="tab-articles"
          >
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            <span className="sm:hidden">Articles ({filteredArticleAudios.length})</span>
            <span className="hidden sm:inline">Article Summaries ({filteredArticleAudios.length})</span>
          </Button>
          {filteredDigests.length > 0 && (
            <Button
              variant={activeTab === 'digests' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('digests')}
              className={activeTab === 'digests' ? 'bg-sky-600 hover:bg-sky-700 text-white' : ''}
              data-testid="tab-digests"
            >
              <Music className="h-3.5 w-3.5 mr-1.5" />
              <span className="sm:hidden">Digests ({filteredDigests.length})</span>
              <span className="hidden sm:inline">Combined Digests ({filteredDigests.length})</span>
            </Button>
          )}
        </div>

        {/* ---- Individual Article Audio Summaries ---- */}
        {activeTab === 'articles' && (
          <>
            {/* Nav V2: Article-not-found empty state */}
            {isArticleRoute && focusedPmid && !loading && !filteredArticleAudios.some((a: any) => a.pmid === focusedPmid) && (
              <Card className="mb-4" data-testid="litstream-article-not-found">
                <CardContent className="py-10 text-center">
                  <Headphones className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-800 mb-1">No audio summary available for this article in LitStream yet.</h3>
                  <p className="text-sm text-slate-500 mb-3">PMID {focusedPmid}</p>
                  <button onClick={() => navigate(selectedProfileId ? `/audio-digests/digests/${selectedProfileId}` : '/audio-digests')}
                    className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                    ← Back to LitStream
                  </button>
                </CardContent>
              </Card>
            )}
            {filteredArticleAudios.length === 0 && !(isArticleRoute && focusedPmid) ? (
              <Card>
                <CardContent className="py-14 text-center">
                  <Headphones className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                  <p className="font-medium text-slate-700" data-testid="empty-state-text">
                    {selectedProfileId ? 'No audio summaries for this specialty' : 'No audio summaries yet'}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    Go to your Library and click "Audio Summary" on any article to generate one.
                  </p>
                  <Button className="mt-4 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => navigate('/library/shelf')}>
                    <BookOpen className="h-4 w-4 mr-1.5" />Go to Library
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredArticleAudios.map((item: any) => {
                  const isPlaying = playingId === item.pmid;
                  return (
                    <Card
                      key={item.pmid}
                      className={`hover:border-sky-200 hover:shadow-sm transition-all border-slate-200 ${isPlaying ? 'border-sky-300 bg-sky-50/30' : ''}`}
                      data-testid={`article-audio-card-${item.pmid}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                            <Headphones className="h-5 w-5 text-sky-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="responsive-title font-semibold text-slate-900 truncate">{item.title}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              {item.journal && <span>{item.journal}</span>}
                              {item.pub_date && <><span>·</span><span>{item.pub_date}</span></>}
                              {item.duration_seconds && (
                                <>
                                  <span>·</span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {Math.floor(item.duration_seconds / 60)}:{String(Math.round(item.duration_seconds % 60)).padStart(2, '0')}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-1">
                              PMID: {item.pmid}
                            </div>
                          </div>
                        </div>
                        {/* Action row */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePlayArticle(item.pmid, item.audio_url)}
                            className={`text-xs ${isPlaying ? 'text-sky-700 border-sky-300 bg-sky-50' : 'text-sky-600 border-sky-200 hover:bg-sky-50'}`}
                            data-testid={`play-article-btn-${item.pmid}`}
                          >
                            {isPlaying ? (
                              <><Pause className="h-3.5 w-3.5 mr-1" />Playing</>
                            ) : (
                              <><Play className="h-3.5 w-3.5 mr-1" />Play</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadArticle(item.pmid, item.audio_url, item.title)}
                            className="text-xs text-slate-600 border-slate-200 hover:bg-slate-50"
                            data-testid={`download-article-btn-${item.pmid}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            Download
                          </Button>
                        </div>
                        <CrossAppLinks
                          pmid={item.pmid}
                          profileId={selectedProfileId}
                          originApp="audio-digests"
                          title={item.title}
                          className="mt-0 px-0 border-t border-slate-100"
                        />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ---- Combined Audio Digests ---- */}
        {activeTab === 'digests' && (
          <>
            {/* Filters */}
        {digests.length > 0 && (
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Filter className="h-4 w-4" />
                  <span className="font-medium">Filters:</span>
                </div>

                {/* Specialty Filter */}
                <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
                  <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="specialty-filter">
                    <Stethoscope className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    {availableSpecialties.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Date Filter */}
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-[150px] h-9" data-testid="date-filter">
                    <Calendar className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-slate-500 hover:text-slate-700"
                    data-testid="clear-filters-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Clear
                  </Button>
                )}

                {/* Results count */}
                <span className="ml-auto text-xs text-slate-400">
                  {filteredDigests.length} of {digests.length} audio summaries
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audio Digest List */}
        {filteredDigests.length === 0 && digests.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center">
              <Music className="h-12 w-12 text-slate-200 mx-auto mb-4" />
              <p className="font-medium text-slate-700" data-testid="empty-state-text">No audio summaries yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Go to your Library, select articles, and create an audio summary.
              </p>
              <Button className="mt-4 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => navigate('/library/shelf')}>
                <BookOpen className="h-4 w-4 mr-1.5" />Go to Library
              </Button>
            </CardContent>
          </Card>
        ) : filteredDigests.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Filter className="h-10 w-10 text-slate-200 mx-auto mb-3" />
              <p className="font-medium text-slate-600">No audio summaries match your filters</p>
              <p className="text-sm text-slate-400 mt-1">Try adjusting your filters or clear them</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters}>
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredDigests.map(d => {
              const isCombined = d.mode === 'combined_summary';
              const isPlaying = playingId === d.audio_digest_id;
              const isDeleting = deletingId === d.audio_digest_id;

              return (
                <Card
                  key={d.audio_digest_id}
                  className={`hover:border-sky-200 hover:shadow-sm transition-all border-slate-200 ${isPlaying ? 'border-sky-300 bg-sky-50/30' : ''}`}
                  data-testid={`audio-digest-card-${d.audio_digest_id}`}
                >
                  <CardContent className="p-4">
                    {/* Top row: info + mode badge */}
                    <div
                      className="flex items-start gap-4 cursor-pointer"
                      onClick={() => navigate(`/audio-digests/${d.audio_digest_id}`)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                        <Headphones className="h-5 w-5 text-sky-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="responsive-title font-semibold text-slate-900 truncate">{d.title}</p>
                          <Badge
                            variant="secondary"
                            data-testid={`audio-digest-mode-badge-${d.audio_digest_id}`}
                            className={`text-[10px] shrink-0 ${
                              isCombined
                                ? 'bg-violet-50 text-violet-700'
                                : 'bg-sky-50 text-sky-700'
                            }`}
                          >
                            {isCombined ? 'Combined Summary' : 'Playlist'}
                          </Badge>
                        </div>
                        {/* Specialty/Subspecialty Tags */}
                        {(d.specialty_name || d.subspecialty_name) && (
                          <div className="flex items-center gap-1.5 mt-1">
                            {d.specialty_name && (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"
                                data-testid={`specialty-badge-${d.audio_digest_id}`}
                              >
                                <Stethoscope className="h-2.5 w-2.5 mr-1" />
                                {d.specialty_name}
                              </Badge>
                            )}
                            {d.subspecialty_name && (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-teal-50 text-teal-700 border-teal-200"
                                data-testid={`subspecialty-badge-${d.audio_digest_id}`}
                              >
                                {d.subspecialty_name}
                              </Badge>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <BookOpen className="h-3 w-3" />{d.item_count} articles
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />{formatDate(d.created_at)}
                          </span>
                          {d.last_played_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />Played {formatDate(d.last_played_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action row: Play / Download / Delete */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                      {/* Play button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleQuickPlay(d); }}
                        className={`text-xs ${isPlaying ? 'text-sky-700 border-sky-300 bg-sky-50' : 'text-sky-600 border-sky-200 hover:bg-sky-50'}`}
                        data-testid={`play-btn-${d.audio_digest_id}`}
                      >
                        {isPlaying ? (
                          <><Pause className="h-3.5 w-3.5 mr-1" />Playing</>
                        ) : (
                          <><Play className="h-3.5 w-3.5 mr-1" />{isCombined ? 'Play Summary' : 'Play Playlist'}</>
                        )}
                      </Button>

                      {/* Download button (playlist mode only) */}
                      {!isCombined && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDownload(d); }}
                          className="text-xs text-slate-600 border-slate-200 hover:bg-slate-50"
                          data-testid={`download-btn-${d.audio_digest_id}`}
                        >
                          <Download className="h-3.5 w-3.5 mr-1" />
                          Download ZIP
                        </Button>
                      )}

                      {/* Spacer */}
                      <div className="flex-1" />

                      {/* Delete button with confirmation */}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isDeleting}
                        className="text-xs text-slate-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-btn-${d.audio_digest_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteId(d.audio_digest_id);
                          setConfirmDeleteTitle(d.title);
                        }}
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDeleteId} onOpenChange={(open: boolean) => { if (!open) setConfirmDeleteId(null); }}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Audio Summary?</DialogTitle>
            <DialogDescription>
              This will remove &ldquo;{confirmDeleteTitle}&rdquo; from your audio summaries. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)} data-testid="delete-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { if (confirmDeleteId) handleDelete(confirmDeleteId); setConfirmDeleteId(null); }}
              data-testid="delete-confirm-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Detail / playback view ──────────────────────────────────────────────────

const AudioDigestDetail: React.FC = () => {
  const navigate = useNavigate();
  const { audioDigestId } = useParams<{ audioDigestId: string }>();
  const [digest, setDigest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!audioDigestId) return;
    audioDigestsAPI.get(audioDigestId)
      .then(setDigest)
      .catch(() => toast.error('Failed to load audio summary'))
      .finally(() => setLoading(false));
  }, [audioDigestId]);

  const handleDownload = async () => {
    if (!audioDigestId) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const url = audioDigestsAPI.downloadZipUrl(audioDigestId);
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        if (body?.detail?.error_code === 'no_audio_ready') {
          toast.error('No audio tracks are ready yet. Generate audio first.');
        } else {
          toast.error('Download failed');
        }
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `litpulse_audio_digest.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('Download started');
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!audioDigestId) return;
    setDeleting(true);
    try {
      await audioDigestsAPI.delete(audioDigestId);
      toast.success('Audio summary deleted');
      navigate('/audio-digests');
    } catch {
      toast.error('Failed to delete');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }
  if (!digest) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-slate-500">Audio summary not found.</p>
      </div>
    );
  }

  const readyCount = digest.counts?.ready || 0;
  const isCombined = digest.mode === 'combined_summary';

  return (
    <div className="flex flex-col" data-testid="audio-digest-detail">
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
          <Button variant="ghost" size="sm" onClick={() => navigate('/audio-digests')} className="mb-3 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1.5" />All Audio Summaries
          </Button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="responsive-title text-xl font-bold text-slate-900" data-testid="digest-detail-title">{digest.title}</h1>
                <Badge
                  variant="secondary"
                  data-testid="audio-digest-detail-mode-badge"
                  className={`text-xs ${isCombined ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'}`}
                >
                  {isCombined ? 'Combined Summary' : 'Playlist'}
                </Badge>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {isCombined ? digest.pmids?.length || 0 : digest.item_count} articles &middot;{' '}
                {new Date(digest.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {isCombined && digest.grounding_level && (
                  <> &middot; <span className="text-violet-600">{digest.grounding_level === 'abstract_only' ? 'Abstract-grounded' : 'Full text available'}</span></>
                )}
              </p>
              {/* Specialty/Subspecialty Tags */}
              {(digest.specialty_name || digest.subspecialty_name) && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  {digest.specialty_name && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                      data-testid="detail-specialty-badge"
                    >
                      <Stethoscope className="h-3 w-3 mr-1" />
                      {digest.specialty_name}
                    </Badge>
                  )}
                  {digest.subspecialty_name && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-teal-50 text-teal-700 border-teal-200"
                      data-testid="detail-subspecialty-badge"
                    >
                      {digest.subspecialty_name}
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {isCombined ? (
                <Button
                  onClick={() => {
                    if (digest.combined_audio_url) setShowPlayer(true);
                  }}
                  disabled={!digest.combined_audio_url}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                  data-testid="audio-digest-play-btn"
                >
                  <Play className="h-4 w-4 mr-1.5" />Play Summary
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => setShowPlayer(true)}
                    disabled={readyCount === 0}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="audio-digest-play-btn"
                  >
                    <Play className="h-4 w-4 mr-1.5" />Play ({readyCount}/{digest.item_count})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownload}
                    disabled={downloading || readyCount === 0}
                    data-testid="audio-digest-download-btn"
                  >
                    {downloading
                      ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      : <Download className="h-4 w-4 mr-1.5" />}
                    Download ZIP
                  </Button>
                </>
              )}
              {/* Delete button with confirmation */}
              <Button
                variant="outline"
                disabled={deleting}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                data-testid="audio-digest-delete-btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-1.5" />
                )}
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {isCombined ? (
          /* Combined Summary view */
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Headphones className="h-4 w-4 text-violet-600" />
                Combined Summary
                {digest.grounding_level && (
                  <Badge variant="secondary" className="ml-auto text-xs bg-violet-50 text-violet-600">
                    {digest.grounding_level === 'abstract_only' ? 'Abstract-grounded' : 'Full text'}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Audio player for combined */}
              {digest.combined_audio_url && (
                <audio
                  controls
                  src={digest.combined_audio_url}
                  className="w-full"
                  data-testid="combined-audio-player"
                />
              )}
              {/* Evidence map: articles in this summary */}
              {digest.evidence_map && digest.evidence_map.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Articles in this summary</p>
                  {digest.evidence_map.map((item: any, i: number) => (
                    <div key={item.pmid} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 text-sm">
                      <span className="text-xs text-slate-400 w-5 shrink-0 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="responsive-title truncate font-medium text-slate-800">{item.title}</p>
                        <p className="text-xs text-slate-400">PMID: {item.pmid}</p>
                      </div>
                      <Badge
                        variant="secondary"
                        className="text-[10px] shrink-0 bg-violet-50 text-violet-600"
                      >
                        {item.grounding_level === 'abstract_only' ? 'Abstract' : 'Full text'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              {/* Transcript */}
              {digest.combined_transcript && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Transcript</p>
                  <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap max-h-96 overflow-y-auto" data-testid="combined-transcript">
                    {digest.combined_transcript}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Playlist view (original) */
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Headphones className="h-4 w-4 text-sky-600" />
                Playlist
                <Badge variant="secondary" className="ml-auto text-xs">
                  {readyCount}/{digest.item_count} ready
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {(digest.items || []).map((item: any, i: number) => (
                <div
                  key={item.pmid}
                  className={`flex items-center gap-3 p-2.5 rounded-lg text-sm
                    ${item.status === 'ready' ? 'bg-slate-50' : 'opacity-50'}`}
                  data-testid={`playlist-item-${item.pmid}`}
                >
                  <span className="text-xs text-slate-400 w-5 shrink-0 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="responsive-title truncate font-medium text-slate-800">{item.title}</p>
                    {item.duration_seconds && (
                      <p className="text-xs text-slate-400">
                        {Math.floor(item.duration_seconds / 60)}:{String(Math.round(item.duration_seconds % 60)).padStart(2, '0')}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant={item.status === 'ready' ? 'secondary' : 'outline'}
                    className={`text-[10px] shrink-0 ${item.status === 'ready' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                  >
                    {item.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {showPlayer && !isCombined && digest.items && (
        <PlaylistModal
          playlist={{ playlist_id: digest.audio_digest_id, items: digest.items, counts: digest.counts }}
          onClose={() => setShowPlayer(false)}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open: boolean) => { if (!open) setShowDeleteConfirm(false); }}>
        <DialogContent data-testid="delete-detail-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Audio Summary?</DialogTitle>
            <DialogDescription>
              This will permanently remove this audio summary. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-testid="delete-detail-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { setShowDeleteConfirm(false); handleDelete(); }}
              data-testid="delete-detail-confirm-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Exports ─────────────────────────────────────────────────────────────────

export { AudioDigestsList, AudioDigestDetail };
